import React, {
  useState,
  useEffect
} from 'react';

import {
  PdfLoader,
  PdfHighlighter,
  Highlight,
  Popup,
  AreaHighlight
} from 'react-pdf-highlighter';

import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Paper,
  Badge,
  Fab,
  Drawer,
  Divider,
  Tooltip,
  Toolbar,
  IconButton,
  Button,
  ButtonBase,
  Typography,
  AppBar,
  Slide,
  CircularProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemAvatar,
  ListItemText,
  TextField,
  Menu,
  MenuItem,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CommentIcon from '@material-ui/icons/Comment';
import CropIcon from '@material-ui/icons/Crop';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import {AnonymousUserView} from './AnonymousUserView';
import getDocumentType from '../../../utils/getDocumentType';
import localization from '../../../utils/localizations';
import {
  Document,
  FileComment,
  SimilarityCheck
} from '../../../types';
import {
  BaseAPI,
} from '../../../data/BaseAPI';
import {
  ObservableAPI,
} from '../../../data/ObservableAPI';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 40,
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-start',
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 9,
  },
  comment: {
    display: 'block',
    width: '100%',
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  commentBody: {
    textAlign: 'left',
    marginBottom: theme.spacing(1),
  },
  commentPageNumber: {
    textAlign: 'right',
    marginTop: theme.spacing(1),
  },
  highlightedPreview: {
    width: '100%',
    border: 'dashed 1px grey',
  },
  selectModeButtons: {
    display: 'none',
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
  },
  deleteButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  commentEditorContainer: {
    width: 300,
  }
}));

const getNextId = () => String(Math.random()).slice(2);

const parseIdFromHash = () => {
  return document.location.hash.slice("#highlight-".length);
}

const resetHash = () => {
  document.location.hash = "";
};

const HighlightPopup = ({ comment }: any) => {
  return (
    comment.text ? (
      <div className="Highlight__popup">
        {comment.emoji} {comment.text}
      </div>
    ) : null
  );
}

function Tip(props: {
  onConfirm: (comment: { text: string, emoji: string }) => void;
  onOpen: () => void;
  onUpdate?: () => void;
}) {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [compact, setCompact] = useState(true);
  const [text, setText] = useState("");
  const [emoji, setEmoji] = useState("");
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);

  const { onConfirm, onOpen } = props;

  return (
    <div className="Tip">
      {compact ? (
        <Button
          variant="contained"
          color="primary"
          type="submit"
          startIcon={<EditIcon />}
          onClick={() => {
            onOpen();
            setCompact(false);
          }}
        >{localization.addComment}</Button>
      ) : (
        <Paper className={classes.commentEditorContainer}>
          <form
            className="Tip__card"
            onSubmit={event => {
              event.preventDefault();
              if (isSaving) return;

              setIsSaving(true);
              onConfirm({ text, emoji });
              setTimeout(() => {
                setIsSaving(false);
              }, 10000);
            }}
          >
            <div>
              <TextField
                style={{width: '100%'}}
                variant="outlined"
                label={localization.comment}
                autoFocus
                multiline
                rows={4}
                value={text}
                onChange={event => setText(event.target.value)}
              />
            </div>
            <div style={{
              marginTop: '10px',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!text || isSaving}
              >{isSaving? 'Saving...' : 'Save'}</Button>

              <IconButton
                onClick={(event: any) => setMoreMenuAnchorEl(event.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
            </div>
          </form>
        </Paper>
      )}
    </div>
  );
}

let scrollViewerTo: any = null;

function PDFAnnotationView(props: {
  doc: Document;
  readOnly?: boolean;
  className?: string;
  showSimilarity?: boolean;
}) {
  const classes = useStyles();

  const [doc, setDoc] = useState(props.doc);
  const [useBlockSelection, setUseBlockSelection] = useState(false);
  const [highlights, setHighlights] = useState([] as any[]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [comments, setComments] = useState([] as FileComment[]);
  const [similarity, setSimilarity] = useState(undefined as SimilarityCheck|undefined);
  const [isSimilarityDrawerOpen, setIsSimilarityDrawerOpen] = useState(false);


  const loadDocument = async (id: string) => {
    const api = new ObservableAPI();
    const url = `files/${id}/`;
    try {
      const result = await api.get(url);
      if (result && (result as any).id) {
        const doc = result as Document;
        let highlights: any[] = doc.comments.map(comment => {
          return generateHighlightFromComment(comment);
        });
    
        setHighlights(highlights);
        setComments(doc.comments);
        setDoc(doc);
        if (doc.comments.length > 0) {
          setIsDrawerOpen(true);
        }
      } 
    } catch (error) {
      console.error(error);
    }
  };

  const loadSimilarity = async (url: string) => {
    const api = new ObservableAPI();
    api.apiEndpoint = '';
    api.path = '';
    try {
      const result = await api.get(url);
      if (result && (result as any).id) {
        const similarity = result as SimilarityCheck;
        similarity.results = similarity.results.sort((a, b) => {
          if (a.similar_words > b.similar_words) return -1;
          if (a.similar_words < b.similar_words) return 1;
          return 0;
        });
        setSimilarity(similarity)
      } 
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    return () => {
      scrollViewerTo = null;
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadDocument(props.doc.id);
    if (props.showSimilarity && props.doc.similarity_url) {
      loadSimilarity(props.doc.similarity_url);
    }
  }, [props.doc.id]);


  const generateHighlightFromComment = (comment: FileComment) => ({
    id: comment.id,
    comment: {
      text: comment.comment,
      emoji: ''
    },
    content: {
      text: comment.highlighted_text,
      image: comment.highlighted_preview,
    },
    position: comment.highlighted_position
  });

  const updateHighlight = (highlightId: string, position: any, content: any) => {
    console.log("Updating highlight", highlightId, position, content, highlights);
    
  }


  const onSubmitComment = async (comment: string, highlighted_text: string, highlighted_preview: string, highlighted_position: any, onSuccess: () => void, onError: () => void) => {
    if (props.readOnly) return;

    const api = new BaseAPI();
    try {
      const [result, response, error] = await api.post({
        comment: comment,
        highlighted_text: highlighted_text,
        highlighted_preview: highlighted_preview,
        highlighted_position: highlighted_position,
        document: props.doc.id,
      }, `files/${props.doc.id}/add_comment/`);
  
      console.log('result', result)
      if (result.success) {
        if (onSuccess) {
          onSuccess();
          loadDocument(props.doc.id);
        }
      }
      else {
        onError();
      }
    } catch (error) {
      console.error(error);
      onError();
    }
  };

  const onRemoveComment = async (comment: FileComment, onSuccess: () => void, onError: () => void) => {
    if (props.readOnly) return;
    if (!window.confirm(localization.removeEntryPrompt + ' ' + comment.comment)) return;

    const api = new BaseAPI();
    try {
      const [result, response, error] = await api.post({
        comment: comment.id,
      }, `files/${props.doc.id}/remove_comment/`);
  
      console.log('result', result)
      if (result.success) {
        if (onSuccess) {
          onSuccess();
          loadDocument(props.doc.id);
        }
      }
      else {
        onError();
      } 
    } catch (error) {
      console.error(error);
      onError();
    }
  };

  if (getDocumentType(props.doc.content_type) !== 'pdf') return (<div className={props.className} />);

  return (
    <div className={`${classes.root} ${props.className}`}>
      <PdfLoader
        url={props.doc.file}
        beforeLoad={<div className={classes.loadingContainer}><CircularProgress disableShrink /></div>}
      >
        {(pdfDocument: any) => (
          <PdfHighlighter
            pdfDocument={pdfDocument}
            enableAreaSelection={(event: any) => {
              console.log('enableAreaSelection', event.altKey, useBlockSelection, event.altKey || useBlockSelection)
              return event.altKey || useBlockSelection;
            }}
            onScrollChange={resetHash}
            scrollRef={(scrollTo: any) => {
              scrollViewerTo = scrollTo;
            }}
            onSelectionFinished={(
              position: any,
              content: any,
              hideTipAndSelection: any,
              transformSelection: any
            ) => {
              console.log('on selection finished', { content, position }, highlights, comments);
              if (props.readOnly) {
                hideTipAndSelection();
                return;
              }

              return (
                <Tip
                  onOpen={transformSelection}
                  onConfirm={(comment: any) => {
                    console.log('on confirm', { content, position, comment }, highlights);
                    onSubmitComment(comment.text, content.text ? content.text : '', content.image ? content.image : '', position, () => {
                      setHighlights([{ content, position, comment, id: getNextId() }, ...highlights]);
                      hideTipAndSelection();
                      setIsDrawerOpen(true);
                      console.log('saved', { content, position, comment }, highlights);
                    }, () => {
                      console.log('error saving', { content, position, comment }, highlights);
                    })
                  }}
                />
              )
            }}
            highlightTransform={(
              highlight: any,
              index: any,
              setTip: any,
              hideTip: any,
              viewportToScaled: any,
              screenshot: any,
              isScrolledTo: any
            ) => {
              const isTextHighlight = !Boolean(
                highlight.content && highlight.content.image
              );

              const component = isTextHighlight ? (
                <Highlight
                  isScrolledTo={isScrolledTo}
                  position={highlight.position}
                  comment={highlight.comment}
                />
              ) : (
                <AreaHighlight
                  highlight={highlight}
                  onChange={(boundingRect: any) => {
                    updateHighlight(
                      highlight.id,
                      { boundingRect: viewportToScaled(boundingRect) },
                      { image: screenshot(boundingRect) }
                    );
                  }}
                />
              );

              return (
                <Popup
                  popupContent={<HighlightPopup {...highlight} />}
                  onMouseOver={(popupContent: any) =>
                    setTip(highlight, (highlight: any) => popupContent)
                  }
                  onMouseOut={hideTip}
                  key={index}
                  children={component}
                />
              );
            }}
            highlights={highlights}
          />
        )}
      </PdfLoader>

      {props.showSimilarity &&
        <Fab
          aria-label={localization.comments}
          variant="extended"
          className={classes.fab}
          onClick={() => setIsSimilarityDrawerOpen(true)}
          style={{left: 16, right: 'auto'}}
        >
          {similarity ? `Similarity: ${similarity.similarity}` : 'Similarity'}
        </Fab>
      }
      <Fab
        color="primary"
        aria-label={localization.comments}
        variant="extended"
        className={classes.fab}
        onClick={() => setIsDrawerOpen(true)}
      >
        <Badge badgeContent={highlights.length} color="secondary">
          <CommentIcon className={classes.extendedIcon} />
        </Badge>
        {localization.comments}
      </Fab>

      {!props.readOnly &&
        <ToggleButtonGroup
          size="small"
          value={useBlockSelection ? 'block' : 'text'}
          exclusive
          onChange={(event: any, selectionMode: string) => {
            setUseBlockSelection(selectionMode === 'block');
          }}
          className={classes.selectModeButtons}
        >
          <ToggleButton value="block">
            <CropIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="text">
            <TextFieldsIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      }

      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={isDrawerOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <Divider />
        {comments.map((comment, i) => {
          return (
            <List key={`comment-list-item-${i}`} className={classes.root}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={<AnonymousUserView
                    id={comment.user.id}
                    onClick={() => {
                      scrollViewerTo(generateHighlightFromComment(comment));
                    }}
                  />}
                  secondary={
                    <ButtonBase
                      className={classes.comment}
                      onClick={() => {
                        scrollViewerTo(generateHighlightFromComment(comment));
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        className={classes.commentBody}
                      >
                        {comment.comment}
                      </Typography>
                      {comment.highlighted_text?.slice(0, 90).trim()}
                      {comment.highlighted_preview && <img src={comment.highlighted_preview} className={classes.highlightedPreview} />}
                      {comment.highlighted_position?.pageNumber &&
                      <div className={classes.commentPageNumber}>Page {comment.highlighted_position.pageNumber}</div>}
                    </ButtonBase>
                  }
                />
                {!props.readOnly &&
                <div className={classes.deleteButtonContainer}>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => {
                      onRemoveComment(comment, () => {

                      }, () => {

                      })
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>}
              </ListItem>
            </List>
          );
        })}
        {!props.readOnly &&
          <>
            <Divider />
            <Alert severity="info">{localization.fileCommentCreationTips}</Alert>
          </>
        }
      </Drawer>

      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={isSimilarityDrawerOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={() => setIsSimilarityDrawerOpen(false)}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <Divider />
        {similarity?.status == 'done' &&
          <List key={`similar-research-list-item`} className={classes.root}>
          {similarity?.results.map((result, i) => {
            return (
                <ListItem key={`similarity-result-${result.id}`} alignItems="flex-start">
                  <ListItemText
                    primary={
                      <ButtonBase
                        onClick={() => {
                          if (result.similar_with_research?.url) {
                            window.open(result.similar_with_research.url, '_blank');
                          }
                        }}
                        style={{textAlign: 'left'}}
                      >{result.similar_with_research?.title_id ? result.similar_with_research?.title_id : result.similar_with_research?.title_en}</ButtonBase>
                    }
                    secondary={result.similarity}
                  />
                </ListItem>
              
            );
          })}
          </List>
        }
        {(!!similarity && similarity?.status != 'done') &&
          <Alert severity="info">Similarity check is in progress.</Alert>
        }
      </Drawer>
    </div>
  );
}

export {PDFAnnotationView};